import { Form, Head } from "@inertiajs/react"
import { ChevronsUpDown } from "lucide-react"
import { useEffect, useRef } from "react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { signInPath, signUpPath } from "@/routes"
import { getMetaContent } from "@/utils/meta"

// Type declaration for Cloudflare Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: { sitekey: string; theme: string },
      ) => string
      remove: (widgetId: string) => void
    }
  }
}

export default function Register() {
  const turnstileWidgetRef = useRef<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Load Cloudflare Turnstile script
    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
    script.async = true
    script.defer = true

    script.onload = () => {
      // Explicit render for Inertia page transitions
      if (window.turnstile && containerRef.current) {
        turnstileWidgetRef.current = window.turnstile.render(
          containerRef.current,
          {
            sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY || "",
            theme: "light",
          },
        )
      }
    }

    document.body.appendChild(script)

    return () => {
      // Cleanup widget on unmount
      if (window.turnstile && turnstileWidgetRef.current) {
        window.turnstile.remove(turnstileWidgetRef.current)
      }
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  return (
    <AuthLayout
      title="Create account"
      description="Click the button below to sign up with Google"
    >
      <Head title="Register" />
      <div className="flex flex-col gap-6">
        {/* <div className="grid gap-2 text-center">
          <p className="text-muted-foreground text-sm">
            We are currently waiting for AWS SES production approval.
          </p>
          <p className="text-muted-foreground text-sm">
            Please sign up with Google below.
          </p>
        </div> */}

        <form action="/auth/google_oauth2" method="post">
          <input
            type="hidden"
            name="authenticity_token"
            value={getMetaContent("csrf-token") || ""}
          />
          <Button type="submit" variant="outline" className="w-full">
            <svg
              className="mr-2 h-4 w-4"
              aria-hidden="true"
              focusable="false"
              data-prefix="fab"
              data-icon="google"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 488 512"
            >
              <path
                fill="currentColor"
                d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
              ></path>
            </svg>
            Sign up with Google
          </Button>
        </form>

        <Collapsible>
          <CollapsibleTrigger asChild>
            <div className="flex w-full items-center justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground gap-2"
              >
                more options coming soon...
                <ChevronsUpDown className="h-4 w-4" />
                <span className="sr-only">Toggle email signup</span>
              </Button>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-6">
            <div className="relative mt-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background text-muted-foreground px-2">
                  OR CONTINUE WITH EMAIL (DISABLED)
                </span>
              </div>
            </div>

            <Form
              method="post"
              action={signUpPath()}
              resetOnSuccess={["password", "password_confirmation"]}
              disableWhileProcessing
              className="flex flex-col gap-6"
            >
              {({ processing, errors }) => (
                <>
                  <div className="grid gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        type="text"
                        name="name"
                        required
                        disabled
                        autoFocus
                        tabIndex={1}
                        autoComplete="name"
                        placeholder="Full name"
                      />
                      <InputError message={errors.name} className="mt-2" />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        required
                        disabled
                        tabIndex={2}
                        autoComplete="email"
                        placeholder="email@example.com"
                      />
                      <InputError message={errors.email} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        name="password"
                        required
                        disabled
                        tabIndex={3}
                        autoComplete="new-password"
                        placeholder="Password"
                      />
                      <InputError message={errors.password} />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="password_confirmation">
                        Confirm password
                      </Label>
                      <Input
                        id="password_confirmation"
                        type="password"
                        name="password_confirmation"
                        required
                        disabled
                        tabIndex={4}
                        autoComplete="new-password"
                        placeholder="Confirm password"
                      />
                      <InputError message={errors.password_confirmation} />
                    </div>

                    {/* Cloudflare Turnstile widget */}
                    <div ref={containerRef} className="flex justify-center" />

                    <Button
                      type="submit"
                      className="mt-2 w-full"
                      tabIndex={5}
                      disabled
                    >
                      {processing && <Spinner />}
                      Create account
                    </Button>
                  </div>

                  <div className="text-muted-foreground text-center text-sm">
                    Already have an account?{" "}
                    <TextLink href={signInPath()} tabIndex={6}>
                      Log in
                    </TextLink>
                  </div>
                </>
              )}
            </Form>
          </CollapsibleContent>
        </Collapsible>
      </div>
    </AuthLayout>
  )
}
